<template>
  <div
    ref="navContent"
    :class="classes"
    class="nav-content-wrapper absolute top-0 left-full pt-0 lg:top-full w-full lg:left-0 nav__content"
    role="none"
  >
    <nav-back-button :label="navBackButtonLabel" class="sticky top-0 z-10" />
    <nav-cards-layout v-if="activeLayout === 'cardLayout'" :nav-item-data="navItemData" />
    <nav-mixed-layout v-else-if="activeLayout === 'mixedCardLayout'" :nav-item-data="navItemData" />
    <nav-accessory-cards-layout v-else-if="activeLayout === 'accessoryCardLayout'" :nav-item-data="navItemData" />
    <nav-b2b-layout v-else-if="activeLayout === 'b2bStyleLayout'" :nav-item-data="navItemData" :is-b2b="isB2b" />
    <nav-default-layout v-else :nav-item-data="navItemData" />
  </div>
</template>

<script>
import NavBackButton from '@/navigation/NavBackButton.vue';
import NavCardsLayout from '@/navigation/NavCardsLayout.vue';
import NavMixedLayout from '@/navigation/NavMixedLayout.vue';
import NavAccessoryCardsLayout from '@/navigation/NavAccessoryCardsLayout.vue';
import NavB2bLayout from '@/navigation/NavB2BLayout.vue';
import NavDefaultLayout from '@/navigation/NavDefaultLayout.vue';

export default {
  name: 'NavContent',
  components: {
    NavBackButton,
    NavCardsLayout,
    NavMixedLayout,
    NavAccessoryCardsLayout,
    NavB2bLayout,
    NavDefaultLayout,
  },
  inject: ['isNavOpen', 'focusParentTier', 'focusNextMenuItem', 'focusPreviousMenuItem'],

  props: {
    navItemData: {
      type: Object,
      default: () => ({}),
    },
    classes: {
      type: String,
      default: '',
    },
    navBackButtonLabel: {
      type: String,
      default: 'Back',
    },
    activeLayout: {
      type: String,
      default: '',
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
    loadNavImages: {
      type: Function,
      default: () => {},
    },
  },
  mounted() {
    this.observeAriaExpanded();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    observeAriaExpanded() {
      const target = this.$refs.navContent.previousElementSibling;
      if (!target) return;

      const observerOptions = {
        attributeFilter: ['aria-expanded'],
        attributeOldValue: true,
      };

      this.observer = new MutationObserver((mutations) => {
        const expanded = mutations[0].target.getAttribute('aria-expanded');
        if (expanded === 'true') {
          this.loadImages();
          this.observer.disconnect(); // Disconnect after first load
        }
      });

      this.observer.observe(target, observerOptions);
    },
    loadImages() {
      return this.loadNavImages(this.$refs.navContent);
    },
    focusPrevious(e) {
      if (!e.target.parentnavItemData.previousElementSibling?.querySelector('.nav__menu-item')) {
        this.focusParentTier(e);
        return;
      }
      const newFocusedItem = e.target.parentnavItemData.previousElementSibling.querySelector('.nav__menu-item');
      e.target.tabIndex = -1;
      newFocusedItem.tabIndex = 0;
      newFocusedItem.focus();
    },
  },
};
</script>
<style lang="scss">
.nav-content-wrapper {
  --nav-content-bg: rgb(255 255 255);
  --nav-focus-offset: 0;

  visibility: hidden;
  pointer-events: none;
  max-height: 100dvh;
  overflow: auto;
  display: block;
  background-color: var(--nav-content-bg);

  @media (screen(lg)) {
    max-height: calc(85vh - var(--nav-tier-1-height, 0px) - var(--header-height));
    background-color: transparent;
    height: auto;
  }

  [aria-expanded='true'] ~ & {
    visibility: visible;
    pointer-events: all;
  }

  &.nav__no-tabs {
    // prettier-ignore
    --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, opacity var(--nav-reduced-motion, 0.2s) ease-in, translate var(--nav-reduced-motion, 0.2s) ease-out, visibility 0s ease var(--nav-reduced-motion, 0.2s);

    color: var(--black);
    visibility: hidden;
    translate: 0 0;
  }

  [aria-expanded='true'] ~ &.nav__no-tabs {
    // prettier-ignore
    --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, opacity var(--nav-reduced-motion, 0.2s) ease-in, translate var(--nav-reduced-motion, 0.2s) ease-out, visibility 0s ease;
    --nav-tier-2-clip: polygon(0 0, 100% 0, 100% 100%, 0 100%);

    visibility: visible;
    opacity: 1;
    pointer-events: all;
  }
}
</style>
